import { mayAnnotate } from './fieldAnnotations';
import api from './api';
import { newUuid } from './collectionRecord';
import { failureOf, putObject } from './uploadPhotos';

/*
 * Voice memos (routes/audio_notes.py, migration 0059): a short recording
 * attached to a field collection, an intake or a specimen.
 *
 * One goes up the way a photograph does -- presign, PUT to storage, then
 * POST /audio-notes -- and the server holds it to the same rules this file
 * states: one of five types, at most 10 MB and 300 s. The recorder
 * (components/VoiceNoteRecorder.js) stops at three minutes.
 *
 * A note waiting to go is plain data plus its Blob:
 *
 *   { id, blob, contentType, durationS, recordedAt }
 *
 * Field Mode keeps the Blob in its device store beside the photographs
 * (utils/fieldBatches.js) and the outbox sends it (utils/fieldOutbox.js);
 * the collection wizard and the sent-record editor send it themselves, once
 * the record exists.
 */

/** The five types the server takes, and the extension each is stored under. */
/** Whether the server takes voice notes from this account: routes/audio_notes.py
 *  refuses the accounts that write nothing (permissions.NON_WRITER_ROLES), as
 *  annotations do -- a public account among them. */
export const mayRecordAudio = mayAnnotate;

export const AUDIO_TYPES = {
  'audio/webm': 'webm', 'audio/ogg': 'ogg', 'audio/mp4': 'm4a', 'audio/mpeg': 'mp3', 'audio/wav': 'wav',
};
const ALIASES = {
  'audio/x-wav': 'audio/wav', 'audio/wave': 'audio/wav', 'audio/vnd.wave': 'audio/wav', 'audio/x-m4a': 'audio/mp4',
  'audio/m4a': 'audio/mp4', 'audio/mp3': 'audio/mpeg', 'audio/x-mpeg': 'audio/mpeg', 'audio/opus': 'audio/ogg',
};
const BY_EXTENSION = { webm: 'audio/webm', ogg: 'audio/ogg', oga: 'audio/ogg', opus: 'audio/ogg', m4a: 'audio/mp4',
  mp4: 'audio/mp4', mp3: 'audio/mpeg', wav: 'audio/wav' };

export const MAX_AUDIO_BYTES = 10 * 1024 * 1024;
export const MAX_AUDIO_SECONDS = 300;
/** Where the recorder stops by itself: well inside the server's limit. */
export const RECORD_CAP_SECONDS = 180;

/** One of AUDIO_TYPES for a recorder's or a file's type ("audio/webm;codecs=opus"
 *  is audio/webm), falling back on the file's extension; '' when none fits. */
export function baseAudioType(type, filename = '') {
  const base = String(type || '').split(';')[0].trim().toLowerCase();
  const named = ALIASES[base] || base;
  if (AUDIO_TYPES[named]) return named;
  const ext = String(filename || '').split('.').pop().toLowerCase();
  return (String(filename).includes('.') && BY_EXTENSION[ext]) || '';
}

/** A note waiting to go, from a recording or a picked file. */
export function newAudioNote({ blob, contentType, durationS = null, recordedAt = null }) {
  return { id: newUuid(), blob, contentType, durationS, recordedAt };
}

/** "1:05" for 65 seconds. */
export function formatDuration(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

const withConfig = (config, headers) => {
  const merged = { ...(config || {}), ...(headers ? { headers: { ...(config?.headers || {}), ...headers } } : {}) };
  return Object.keys(merged).length ? [merged] : [];
};

/**
 * One note: presign, PUT, then POST /audio-notes, for the record `anchor`
 * names ({sample_id} | {external_id} | {specimen_id}).
 *
 * options (as uploadOnePhoto's):
 *   stored          {s3_key, s3_url} of an earlier PUT of this note: only the
 *                   registration is sent
 *   onStored        called with {s3_key, s3_url} once the PUT has landed
 *   idempotencyKey  sent with POST /audio-notes, so a retry whose answer was
 *                   lost is not a second note
 *   requestConfig   merged into every API request's config
 *
 * Resolves {ok: true, audio_id, s3_key, s3_url} or {ok: false, status, code,
 * unreachable}. Never rejects.
 */
export async function uploadAudioNote(note, anchor, options = {}) {
  const { stored, onStored, idempotencyKey, requestConfig } = options;
  const contentType = baseAudioType(note.contentType || note.blob?.type);
  try {
    let where = stored && stored.s3_key ? stored : null;
    if (!where) {
      const issued = (await api.post('/audio-notes/presign', {
        ...anchor, content_type: contentType, bytes: note.blob?.size || undefined,
      }, ...withConfig(requestConfig))).data;
      // The type the presign was made for: S3 signs it, so the PUT must match.
      await putObject(issued.presign_url, note.blob, issued.content_type || contentType);
      where = { s3_key: issued.s3_key, s3_url: issued.s3_url };
      if (onStored) await onStored(where);
    }
    const res = await api.post('/audio-notes', {
      ...anchor,
      s3_key: where.s3_key,
      content_type: contentType,
      bytes: note.blob?.size || 0,
      ...(Number.isFinite(note.durationS) ? { duration_s: Math.round(note.durationS * 10) / 10 } : {}),
      ...(note.recordedAt ? { recorded_at: note.recordedAt } : {}),
    }, ...withConfig(requestConfig, idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : null));
    return { ok: true, audio_id: res?.data?.audio_id ?? null, ...where };
  } catch (e) {
    return { ok: false, ...failureOf(e) };
  }
}

/** Every note in `notes`, in order: {sent: [ids], failed: [ids]}. */
export async function uploadAudioNotes(notes, anchor, keyFor = null) {
  const sent = [];
  const failed = [];
  for (const note of notes || []) {
    const result = await uploadAudioNote(note, anchor, keyFor ? { idempotencyKey: keyFor(note) } : {});
    (result.ok ? sent : failed).push(note.id);
  }
  return { sent, failed };
}

/** A record's notes on the server: 'collections' | 'external' | 'specimens'. */
export function listAudioNotes(kind, id, config) {
  return api.get(`/${kind}/${id}/audio-notes`, ...(config ? [config] : []))
    .then((res) => (Array.isArray(res?.data) ? res.data : []));
}
